import type { Treatment } from '../../types/care';
import {
  LEAFLETS,
  bloodThinners,
  corticosteroidSideEffects,
  injectionResult,
  injectionTimeline,
  reportBasic,
  reportWithPacemaker,
  standardBring,
  standardComplications,
  standardInjectionFacts,
} from './shared';

export const injectionsTrunkLimbs: Treatment[] = [
  {
    slug: 'acnes',
    name: 'ACNES behandeling',
    category: 'Injecties & blokkades',
    summary: 'Behandeling van het buikwandpijnsyndroom door een beknelde zenuw in de buikwand.',
    intro: [
      'Bij het buikwandpijnsyndroom ACNES (Anterior Cutaneous Nerve Entrapment Syndrome) ontstaat pijn doordat huidtakjes van de tussenribzenuwen bekneld raken op de plek waar zij door het strakke kapsel van de rechte buikspier gaan.',
      'De pijn is met één vinger aan te wijzen, verergert bij inspanning van de buikspieren (bukken, hoesten, sporten) en vermindert bij ontspanning. De huid rond de pijnlijke plek kan dof of juist overgevoelig aanvoelen. Omdat ACNES nog vrij onbekend is, wordt vaak eerst een andere diagnose gesteld.',
      'In ruim de helft van de gevallen ontstaat ACNES spontaan; soms na een operatie, zwangerschap of sport.',
    ],
    procedure: [
      'De behandeling hangt af van uw pijnbeeld. Mogelijkheden zijn een injectie met verdovende en irritatieremmende medicatie op de pijnlijke plek, medicamenteuze ondersteuning, TENS of een Qutenza-pleister.',
      'Bij uw eerste bezoek aan de pijnspecialist wordt u uitgebreid geïnformeerd over de behandelmogelijkheden.',
    ],
    facts: { ...standardInjectionFacts },
    report: reportBasic,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/ACNES-VER-FOL-001-OPE-V3.0.pdf`,
  },
  {
    slug: 'carpaal-tunnel-hydrodissectie',
    name: 'Carpaal tunnel syndroom — hydrodissectie',
    category: 'Injecties & blokkades',
    summary: 'Echogeleid vrijmaken van de beknelde middenhandszenuw in de pols, zonder operatie.',
    intro: [
      'Het carpaal tunnel syndroom (CTS) ontstaat doordat de middenhandszenuw (nervus medianus) bekneld raakt in de pols. Door de carpale tunnel aan de kant van de handpalm lopen ook de pezen van de vingers; een zwelling in deze tunnel geeft druk op de zenuw.',
    ],
    procedure: [
      'Met het echoapparaat zoekt de arts de carpale tunnel op en beoordeelt of de zenuw bekneld is. Vervolgens wordt een naald dicht bij de zenuw geplaatst; soms voelt u hierbij een prikkeling.',
      'Met de hydrodissectie-methode wordt een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) tussen het peesblad en de zenuw gespoten, waardoor de zenuw weer ruimte krijgt. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Echogeleid' },
    preparation: [bloodThinners],
    report: reportBasic,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Carpaal-Tunnel-Syndroom-VER-FOL-006-OPE-V3.0.pdf`,
  },
  {
    slug: 'caudale-injectie',
    name: 'Caudale injectie',
    category: 'Injecties & blokkades',
    summary: 'Injectie in de caudale ruimte net boven het staartbeen bij lage rug- en beenpijn.',
    intro: [
      'Bij een caudale injectie worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) in de caudale ruimte gespoten. Deze ruimte bevindt zich onderaan de wervelkolom, net boven het staartbeen, en bevat bindweefsel, bloedvaten, hersenvocht en zenuwen.',
    ],
    procedure: [
      'U ligt op uw buik. Met röntgendoorlichting wordt de plaats van de injectie bepaald. Na plaatselijke verdoving van de huid brengt de arts een naald in de caudale ruimte en spuit de medicatie in.',
      'Dit kan een drukkend gevoel geven of pijnlijk zijn, omdat de medicatie zich moet verdelen. Na ongeveer 30 minuten mag u naar huis.',
      'U kunt tijdelijk minder kracht in uw benen hebben. Dit herstelt zich vanzelf binnen enkele uren.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Röntgendoorlichting' },
    preparation: [bloodThinners],
    report: reportBasic,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Caudale-injectie-VER-FOL-007-OPE-V3.0.pdf`,
  },
  {
    slug: 'epidurale-injectie',
    name: 'Epidurale injectie',
    category: 'Injecties & blokkades',
    summary: 'Verdoving en irritatieremmer in de epidurale ruimte van het wervelkanaal, op nek-, borst- of lage rugniveau.',
    intro: [
      'Een epidurale injectie is een behandeling waarbij een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmend medicijn (corticosteroïd) in de epidurale ruimte worden geïnjecteerd.',
      'De epidurale ruimte ligt tussen de binnenzijde van het wervelkanaal en het harde ruggenmergvlies, en bevat bindweefsel, bloedvaten en zenuwwortels. De behandeling kan op cervicaal, thoracaal of lumbaal niveau worden toegepast, afhankelijk van uw klachtenpatroon.',
    ],
    procedure: [
      'U ligt op uw buik. Wordt de behandeling cervicaal (in de nek) toegepast, dan zit u op een stoel en hangt u iets voorover. Met röntgendoorlichting wordt de plaats van de injectie bepaald.',
      'Na plaatselijke verdoving van de huid brengt de arts een naald in de epidurale ruimte en injecteert de medicatie. Dit kan een drukkend gevoel geven of pijnlijk zijn, omdat de medicatie zich moet verdelen. Na ongeveer 30 minuten mag u naar huis, mits u zich goed voelt.',
      'Afhankelijk van het niveau van de injectie kunt u tijdelijk wat minder kracht in uw benen of armen hebben. Dit herstelt zich binnen enkele uren vanzelf.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Röntgendoorlichting' },
    preparation: [bloodThinners],
    report: reportBasic,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: [
      ...standardComplications,
      'Hoofdpijn — als het ruggenmergvlies wordt aangeprikt. Is deze na een aantal dagen niet verminderd, neem dan contact met ons op.',
    ],
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Epidurale-injectie-VER-FOL-008-OPE-V3.0.pdf`,
  },
  {
    slug: 'durolane',
    name: 'Durolane behandeling',
    category: 'Injecties & blokkades',
    summary: 'Eenmalige injectie met hyaluronzuur in knie of heup bij artrose.',
    intro: [
      'Een Durolane-behandeling is een injectie met hyaluronzuur in het knie- of heupgewricht. Hyaluronzuur komt van nature voor in het gewrichtsvocht en zorgt voor smering en schokdemping.',
      'Door slijtage (artrose) kan de kwaliteit en hoeveelheid gewrichtsvocht afnemen, met pijn, stijfheid en bewegingsbeperking als gevolg. Durolane kan de pijn verminderen en de beweeglijkheid verbeteren.',
    ],
    procedure: [
      'U ligt op uw rug. Onder echogeleiding wordt de naald ingebracht, waarna de arts de medicatie toedient. De behandeling bestaat uit één injectie en duurt slechts enkele minuten.',
      'Na ongeveer 30 minuten mag u naar huis, mits u zich goed voelt.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Echogeleid', duration: 'Enkele minuten' },
    preparation: [bloodThinners],
    report: reportBasic,
    bring: standardBring,
    result: [
      'De eerste dagen kan het gewricht wat gevoelig, warm of licht gezwollen zijn; dit trekt meestal vanzelf weg.',
      'Het effect is niet direct merkbaar: in de dagen tot weken na de injectie neemt de pijn geleidelijk af. De pijnvermindering houdt enkele maanden tot ongeveer een jaar aan. Na ongeveer 12 weken is het effect goed te beoordelen.',
    ],
    complications: standardComplications,
    sideEffects: [{ title: 'Mogelijke bijwerkingen', items: ['Pijn of zwelling in het gewricht', 'Warm of stijf gevoel in het gewricht'] }],
    pdf: `${LEAFLETS}/Durolane-behandeling-VER-FOL-034-OPE-V1.0.pdf`,
  },
  {
    slug: 'intercostaal-blokkade',
    name: 'Intercostaalblokkade (medicamenteus of PRF)',
    category: 'Injecties & blokkades',
    summary: 'Blokkade van de tussenribzenuwen bij pijn aan borstkast, ribben of flank.',
    intro: [
      'Onder iedere rib loopt een zenuw vanuit de rug naar het borstbeen: de tussenribzenuw of intercostaalzenuw. Deze zenuwen kunnen een rol spelen bij pijn aan borstkast, ribben of flank.',
      'Bij een intercostaalblokkade worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) rondom de zenuw gespoten. Waar nodig wordt de zenuw aanvullend met gepulseerde radiofrequente stroom (PRF) behandeld.',
    ],
    procedure: [
      'U ligt op uw buik. Met röntgendoorlichting wordt de te behandelen plek bepaald, waarna de arts een naald dicht bij de zenuw plaatst.',
      'De arts dient de medicatie toe en verwijdert de naald. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Röntgendoorlichting' },
    preparation: [bloodThinners],
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: [
      ...standardComplications,
      'Zeer kleine kans op een klaplong. Verschijnselen zijn hoesten, pijn op de borst of kortademigheid, tot enkele uren na de behandeling.',
    ],
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Intercostaalblokkade-medicamenteus-prf-VER-FOL-011-OPE-V3.0.pdf`,
  },
  {
    slug: 'parasacraal-blokkade',
    name: 'Parasacraal blokkade',
    category: 'Injecties & blokkades',
    summary: 'Blokkade van de zenuwen naast het heiligbeen bij bekken-, bil- en beenpijn.',
    intro: [
      'Bij een parasacrale blokkade worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een ontstekingsremmer (corticosteroïd) rondom de zenuwen ter hoogte van het heiligbeen (sacrum) gespoten.',
      'De behandeling wordt toegepast bij pijn in het bekken, de bilregio of uitstralende pijn naar het been, bijvoorbeeld bij irritatie van de sacrale zenuwen.',
    ],
    procedure: [
      'U ligt op uw buik. De pijnspecialist bepaalt met röntgendoorlichting waar de injectie komt. De huid wordt gedesinfecteerd en de naald zorgvuldig geplaatst.',
      'Na het toedienen van de medicatie blijft u ongeveer 30 minuten ter observatie.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Röntgendoorlichting' },
    report: reportBasic,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Parasacraal-blokkade-VER-FOL-039-OPE-V1.0.pdf`,
  },
  {
    slug: 'pudendus-blokkade',
    name: 'Pudendus blokkade',
    category: 'Injecties & blokkades',
    summary: 'Echogeleide blokkade van de schaamzenuw bij pudendus neuralgie.',
    intro: [
      'Bij een pudendus blokkade worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) rondom de nervus pudendus gespoten.',
      'Bij pudendus neuralgie is de schaamzenuw bekneld of beschadigd. Dit kan klachten geven rond de geslachtsorganen, de liezen en de binnenkant van de bovenbenen: pijn of een brandend gevoel, verminderd gevoel, pijn aan het zitbeen, een tintelend gevoel bij zitten of moeite met het ophouden van urine of ontlasting.',
    ],
    procedure: [
      'U ligt op uw rug. Met het echoapparaat wordt de te behandelen plek bepaald, waarna de arts een naald dicht bij de zenuw plaatst.',
      'De arts dient de medicatie toe en verwijdert de naald. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Echogeleid' },
    preparation: [bloodThinners],
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Pudendus-blokkade-VER-FOL-019-OPE-V3.0.pdf`,
  },
  {
    slug: 'triggerpoint-injectie',
    name: 'Triggerpoint injectie, lokale injectie of facetdenervatie',
    category: 'Injecties & blokkades',
    summary: 'Eén of meer injecties op de plek waar u de meeste pijn heeft.',
    intro: [
      'Bij een triggerpoint injectie worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) gespoten op de plek waar u de meeste pijn heeft. U krijgt één of meerdere injecties.',
    ],
    procedure: [
      'U zit of ligt op de behandeltafel. De pijnspecialist vraagt u de pijnlijke plek zo precies mogelijk aan te wijzen en markeert deze met een stift. Sommige injecties worden onder röntgendoorlichting of echografie gegeven.',
      'De naald wordt in het te behandelen gebied geplaatst, de medicatie toegediend en de naald verwijderd. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Op geleide van palpatie, echo of röntgen' },
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Trigger-point-injectie-of-lokale-injectie-facetdenervatie-VER-FOL-025-OPE-V3.0.pdf`,
  },
  {
    slug: 'ncfl-blokkade',
    name: 'Zenuwblokkade NCFL (huidzenuw bovenbeen)',
    category: 'Injecties & blokkades',
    summary: 'Blokkade van de nervus cutaneus femoris lateralis bij een dove of tintelende plek op het bovenbeen.',
    intro: [
      'Deze injectie wordt in de lies gegeven bij de nervus cutaneus femoris lateralis. Deze huidzenuw loopt vanuit het bekken door de liesband naar de huid aan de zij- en buitenkant van het bovenbeen, van lies tot knie.',
      'De typische klacht is een dove, pijnlijke of tintelende plek aan de zijkant van het bovenbeen.',
    ],
    procedure: [
      'U ligt op uw rug. Met het echoapparaat wordt de te behandelen plek bepaald, waarna de arts een naald dicht bij de zenuw plaatst.',
      'De medicatie wordt toegediend en de naald verwijderd. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Echogeleid' },
    preparation: [bloodThinners],
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/zenuwblokkade-NCFL-VER-FOL-026-OPE-V3.0.pdf`,
  },
  {
    slug: 'ilio-inguinalis',
    name: 'Zenuwblokkade bij liespijn (nervus ilio-inguinalis)',
    category: 'Injecties & blokkades',
    summary: 'Echogeleide blokkade van de zenuw die tussen de buikspieren naar de lies loopt.',
    intro: [
      'Bij een blokkade van de nervus ilio-inguinalis worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) rondom de zenuw gespoten. Deze zenuw loopt tussen de buikspieren door naar de lies.',
    ],
    procedure: [
      'Met het echoapparaat wordt de te behandelen plek bepaald, waarna de arts een naald bij de zenuw plaatst.',
      'De arts dient de medicatie toe en verwijdert de naald. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Echogeleid' },
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Zenuwblokkade-bij-liespijn-injectie-nervus-ilio-inguinalis-VER-FOL-027-OPE-V3.0.pdf`,
  },
  {
    slug: 'piriformis-blokkade',
    name: 'Zenuwblokkade piriformis',
    category: 'Injecties & blokkades',
    summary: 'Echogeleide injectie in de piriformisspier bij pijn in bil, dijbeen en knieholte.',
    intro: [
      'Bij een piriformis blokkade worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) in de piriformisspier gespoten. Deze spier loopt van het heiligbeen naar het dijbeen en draait het bovenbeen naar buiten.',
      'Als de spier door een val of onbekende oorzaak gezwollen is of verkrampt, kan zij geïrriteerd raken. U kunt dan zeurende pijn diep in de bil hebben, met uitstraling naar de rug en de achterkant van het dijbeen tot in de knieholte — vaak erger bij lang zitten.',
    ],
    procedure: [
      'U ligt op uw buik. De pijnspecialist zoekt met het echoapparaat de piriformisspier op en plaatst de naald onder echogeleiding.',
      'De medicatie wordt toegediend en de naald verwijderd. Na ongeveer 30 minuten mag u naar huis. Door de verdoving kunt u een aantal uren minder kracht in uw been hebben.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Echogeleid' },
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Zenuwblokkade-piriformis-VER-FOL-028-OPE-V2.0.pdf`,
  },
  {
    slug: 'zenuwwortel-rug',
    name: 'Zenuwwortelblokkade bij rug- en/of beenklachten',
    category: 'Injecties & blokkades',
    summary: 'Gerichte injectie bij een geprikkelde zenuwwortel in de lage rug, onder röntgendoorlichting.',
    intro: [
      'Een zenuwwortelblokkade is een behandeling waarbij een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) rondom de zenuwwortel worden gespoten. Bij uitstralende pijn naar het been is vaak één zenuwwortel geprikkeld, bijvoorbeeld door een hernia.',
    ],
    procedure: [
      'U ligt op uw buik. Met röntgendoorlichting wordt de plek bepaald, waarna de arts een naald aan de zijkant van de wervelkolom plaatst, dicht bij de zenuwwortel.',
      'Met contrastvloeistof wordt de positie van de naald gecontroleerd. Daarna wordt de medicatie toegediend en de naald verwijderd. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Röntgendoorlichting' },
    preparation: [bloodThinners],
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Zenuwwortelblokkade-bij-rug-en-of-beenklachten-VER-FOL-031-OPE-V3.0.pdf`,
  },
];
